import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { TablePagination } from "@/components/ui/table-pagination";
import { useProjectMembershipsPaginated } from "@/hooks/useProjectMembershipsPaginated";
import { useMembers, type Member } from "@/hooks/useMembers";
import { formatDate } from "@/lib/date";

interface TeamSectionProps {
  projectSlug: string;
}

export function TeamSection({ projectSlug }: TeamSectionProps) {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // SD-2: pass projectSlug directly -- server resolves slug-or-id
  const { memberships, total, loading, error } =
    useProjectMembershipsPaginated(projectSlug, page, pageSize);

  // SD-3: client-side join for name/email
  const { members } = useMembers();
  const memberMap = useMemo(
    () => new Map<string, Member>(members.map((m) => [m.id, m])),
    [members],
  );

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-4 text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span>{t("projectPortal.config.team.loading")}</span>
      </div>
    );
  }

  if (error) {
    return <p className="py-4 text-sm text-destructive">{error}</p>;
  }

  if (memberships.length === 0) {
    return (
      <p className="py-4 text-sm text-muted-foreground">
        {t("projectPortal.config.team.empty")}
      </p>
    );
  }

  return (
    <div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t("projectPortal.config.team.name")}</TableHead>
            <TableHead>{t("projectPortal.config.team.email")}</TableHead>
            <TableHead>{t("projectPortal.config.team.role")}</TableHead>
            <TableHead>{t("projectPortal.config.team.joined")}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {memberships.map((membership) => {
            const member = memberMap.get(membership.member_id);
            return (
              <TableRow key={membership.id}>
                <TableCell>
                  {member?.name ?? (
                    <span className="font-mono text-xs">
                      {membership.member_id}
                    </span>
                  )}
                </TableCell>
                <TableCell>{member?.email ?? "—"}</TableCell>
                <TableCell>{membership.project_role}</TableCell>
                <TableCell>{formatDate(membership.created_at)}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      <TablePagination
        currentPage={page}
        totalPages={totalPages}
        onPrev={() => setPage((p) => Math.max(1, p - 1))}
        onNext={() => setPage((p) => Math.min(totalPages, p + 1))}
        hasNext={page < totalPages}
        hasPrev={page > 1}
        pageSize={pageSize}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPage(1);
        }}
      />
    </div>
  );
}
